import Moment from 'moment';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { find } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';

import DateRangeInputMenuList from './DateRangeInputMenuList';
import ListItem from '../../data-display/ListItem';
import LoadingSpinner from '../../utils/LoadingSpinner';
import SelectInput from '../SelectInput';
import { DateRangeProvider } from './use-date-range';
import { formatDateRange } from '../../../../libraries/formatters';

import type { DateRange, Option } from './types';
import type { FormatOptionLabelMeta } from 'react-select/dist/declarations/src/Select';
import type { OnChangeValue } from 'react-select/dist/declarations/src/types';
import type { ReactNode } from 'react';

const DEFAULT_OPTIONS: Option[] = [{
  label: 'This year',
  value: 'ytd',
  startDate: Moment().startOf('year').format(),
  endDate: Moment().endOf('year').format(),
}, {
  label: 'This month',
  value: 'mtd',
  startDate: Moment().startOf('month').format(),
  endDate: Moment().endOf('month').format(),
}, {
  label: 'This week',
  value: 'wtd',
  startDate: Moment().startOf('isoWeek').format(),
  endDate: Moment().startOf('isoWeek').add(4, 'days').format(),
}];

export enum DateRangeInputLayout {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

interface Props {
  allowEmptyEndDate?: boolean;
  className?: string;
  defaultOptions?: Option[];
  disablePastDates?: boolean;
  helperText?: ReactNode;
  id?: string;
  isAlwaysCustom?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  label?: string;
  layout?: `${DateRangeInputLayout}`;
  onChange: (newValue: OnChangeValue<Option, false>) => void;
  placeholder?: string;
  selectedDateRange: Option;
}

const DateRangeInput = ({
  allowEmptyEndDate = true,
  className,
  defaultOptions = DEFAULT_OPTIONS,
  disablePastDates = false,
  helperText,
  id,
  isAlwaysCustom = false,
  isClearable = true,
  isDisabled = false,
  isLoading = false,
  label,
  layout = DateRangeInputLayout.Vertical,
  onChange,
  placeholder = 'All time',
  selectedDateRange,
}: Props) => {
  const options = useMemo<Option[]>(() => [...(isAlwaysCustom ? [] : defaultOptions), {
    label: 'Custom range',
    value: 'custom',
  }], [defaultOptions]);

  const calendarIcon = useMemo(() => (isLoading ?
    <LoadingSpinner /> :
    <FontAwesomeIcon icon={faCalendarAlt} />
  ), [isLoading]);

  useEffect(() => {
    if (selectedDateRange.value && selectedDateRange.value !== 'custom') {
      const selectedOption = find(options, ['value', selectedDateRange.value]);
      if (selectedOption && (!Moment(selectedOption.startDate).isSame(selectedDateRange.startDate) || !Moment(selectedOption.endDate).isSame(selectedDateRange.endDate))) {
        onChange(selectedOption);
      }
    }
  }, [selectedDateRange.value]);

  const formatOptionLabel = useCallback((option: Option, { context }: FormatOptionLabelMeta<Option>) => {
    if (context !== 'menu' && isAlwaysCustom) {
      if (!selectedDateRange?.startDate) {
        return <span>{placeholder}</span>;
      }
      return formatDateRange(selectedDateRange.startDate, selectedDateRange.endDate);
    }
    if (context !== 'menu' && option.value === 'custom') {
      return (
        <span>
          {option.label}&nbsp;({formatDateRange(selectedDateRange.startDate, selectedDateRange.endDate)})
        </span>
      );
    }

    if (context === 'menu' && option.value !== 'custom') {
      return (
        <ListItem
          label={option.label}
          secondaryText={formatDateRange(option.startDate, option.endDate)}
        />
      );
    }

    return option.label;
  }, [selectedDateRange]);

  const setDateRange = useCallback((days: DateRange) => {
    onChange({ value: 'custom', ...days });
  }, [onChange]);

  return (
    <DateRangeProvider
      dateRange={selectedDateRange}
      disablePastDates={disablePastDates}
      isAlwaysCustom={isAlwaysCustom}
      setDateRange={setDateRange}
    >
      <SelectInput<string, Option, false>
        className={classnames('date-range-input', `layout-${layout}`, className)}
        closeMenuOnSelect={false}
        components={{
          MenuList: DateRangeInputMenuList,
        }}
        formatOptionLabel={formatOptionLabel}
        helperText={helperText}
        icon={calendarIcon}
        id={id}
        isClearable={isClearable}
        isDisabled={isDisabled}
        label={label}
        onChange={onChange}
        onMenuClose={(
          allowEmptyEndDate ?
            undefined :
            () => {
              if (!selectedDateRange.endDate) {
                setDateRange({
                  startDate: selectedDateRange.startDate,
                  endDate: selectedDateRange.startDate,
                });
              }
            }
        )}
        options={options}
        placeholder={placeholder}
        value={selectedDateRange.value ? find(options, ['value', selectedDateRange.value]) : undefined}
      />
    </DateRangeProvider>
  );
};

export default DateRangeInput;
